import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { toast } from 'react-toastify'

import {
  HTML_BLOCK_LOAD_TIMEOUT,
  HTML_BLOCK_MAX_HEIGHT,
  HTML_BLOCK_MIN_HEIGHT,
} from '../consts/html-block-policy'
import { IHtmlBlockVisibility } from '../services/html-block-visibility.service'
import { maxDebug } from '../utils/max-debug'
import { IHtmlBlockFrameRegistry } from '../utils/html-block/types'

export type UseHtmlBlockFrameOptions = {
  frameId: string
  frameRegistry: IHtmlBlockFrameRegistry
  hasRenderableSource: boolean
  srcdoc: string
  visibility?: IHtmlBlockVisibility
}

export type HtmlBlockFrameController = {
  frameRef: (element: HTMLIFrameElement | null) => void
  frameSrcdoc: string
  frameHeight: number
  reportedHeight: number
  hasReportedHeight: boolean
  loaded: boolean
  showSkeleton: boolean
  handleLoad: () => void
  handlePrint: () => void
}

/**
 * Own an isolated HTML block frame: activation, height reporting, skeleton state,
 * release under visibility pressure and the print request.
 */
export const useHtmlBlockFrame = ({
  frameId,
  frameRegistry,
  hasRenderableSource,
  srcdoc,
  visibility,
}: UseHtmlBlockFrameOptions): HtmlBlockFrameController => {
  const [frameElement, setFrameElement] = useState<HTMLIFrameElement | null>(null)
  const [frameSrcdoc, setFrameSrcdoc] = useState('')
  const [reportedHeight, setReportedHeight] = useState(HTML_BLOCK_MIN_HEIGHT)
  const [hasReportedHeight, setHasReportedHeight] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [showSkeleton, setShowSkeleton] = useState(true)

  // The in-place frame is clipped at the clamp; the overlay uses reportedHeight unclamped.
  const frameHeight = useMemo(
    () => Math.min(HTML_BLOCK_MAX_HEIGHT, Math.max(HTML_BLOCK_MIN_HEIGHT, reportedHeight)),
    [reportedHeight]
  )

  const activeRef = useRef(false)
  const loadedRef = useRef(false)
  const hasReportedHeightRef = useRef(false)
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Latest-value mirrors so the observer callbacks registered once stay accurate.
  const srcdocRef = useRef(srcdoc)
  srcdocRef.current = srcdoc
  const hasRenderableSourceRef = useRef(hasRenderableSource)
  hasRenderableSourceRef.current = hasRenderableSource
  const frameSrcdocRef = useRef(frameSrcdoc)
  frameSrcdocRef.current = frameSrcdoc

  const clearLoadTimeout = useCallback((): void => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current)
      loadTimeoutRef.current = null
    }
  }, [])

  const startLoadTimeout = useCallback((): void => {
    clearLoadTimeout()
    loadTimeoutRef.current = setTimeout(() => {
      if (hasReportedHeightRef.current) {
        return
      }

      setShowSkeleton(false)
      maxDebug.warn(`[HtmlBlockFrame] Frame ${frameId} did not report a height within 3 seconds`)
    }, HTML_BLOCK_LOAD_TIMEOUT)
  }, [clearLoadTimeout, frameId])

  const release = useCallback((): void => {
    if (!activeRef.current) {
      return
    }

    clearLoadTimeout()
    activeRef.current = false
    loadedRef.current = false
    hasReportedHeightRef.current = false
    setLoaded(false)
    setHasReportedHeight(false)
    setShowSkeleton(true)
    setFrameSrcdoc('')
    visibility?.release(frameId)
  }, [clearLoadTimeout, frameId, visibility])

  const activate = useCallback((): void => {
    if (activeRef.current || !hasRenderableSourceRef.current || !srcdocRef.current) {
      return
    }

    visibility?.claim(frameId, release)
    activeRef.current = true
    loadedRef.current = false
    hasReportedHeightRef.current = false
    setFrameSrcdoc(srcdocRef.current)
    setLoaded(false)
    setHasReportedHeight(false)
    setShowSkeleton(true)
    // The next height report must not be silently dropped by the registry's
    // oscillation guard just because it matches whatever was reported before
    // this frame was released — that would leave hasReportedHeight stuck at
    // false (Maximize permanently disabled) even though the frame reloaded fine.
    frameRegistry.resetHeightTracking(frameId)
    startLoadTimeout()
  }, [frameId, frameRegistry, release, startLoadTimeout, visibility])

  const handleHeight = useCallback(
    (height: number): void => {
      if (!activeRef.current) {
        return
      }

      hasReportedHeightRef.current = true
      setReportedHeight(height)
      setHasReportedHeight(true)
      setShowSkeleton(false)
      clearLoadTimeout()
    },
    [clearLoadTimeout]
  )

  const resetLoadingState = useCallback((): void => {
    loadedRef.current = false
    hasReportedHeightRef.current = false
    setReportedHeight(HTML_BLOCK_MIN_HEIGHT)
    setHasReportedHeight(false)
    setLoaded(false)
    setShowSkeleton(true)
    frameRegistry.resetHeightTracking(frameId)

    if (activeRef.current) {
      setFrameSrcdoc(srcdocRef.current)
      startLoadTimeout()
    } else {
      setFrameSrcdoc('')
    }
  }, [frameId, frameRegistry, startLoadTimeout])

  // Registration follows the frame element and the renderable source, the way the
  // component lifecycle owns it: register on mount, tear everything down on release.
  useEffect(() => {
    if (!hasRenderableSource || !frameElement) {
      return undefined
    }

    const unregisterFrame = frameRegistry.register(frameId, frameElement, handleHeight)
    let stopLoadObservation: (() => void) | null = null
    let stopReleaseObservation: (() => void) | null = null

    if (visibility) {
      stopLoadObservation = visibility.observeLoad(frameElement, activate)
      stopReleaseObservation = visibility.observeRelease(frameId, frameElement, release, () =>
        visibility.touch(frameId)
      )
    } else {
      activate()
    }

    return () => {
      clearLoadTimeout()
      stopLoadObservation?.()
      stopReleaseObservation?.()
      unregisterFrame()
      activeRef.current = false
      loadedRef.current = false
      hasReportedHeightRef.current = false
      setReportedHeight(HTML_BLOCK_MIN_HEIGHT)
      setHasReportedHeight(false)
      setLoaded(false)
      setShowSkeleton(true)
      setFrameSrcdoc('')
      visibility?.release(frameId)
    }
  }, [
    activate,
    clearLoadTimeout,
    frameElement,
    frameId,
    frameRegistry,
    handleHeight,
    hasRenderableSource,
    release,
    visibility,
  ])

  // A new document for an already mounted frame restarts the load cycle.
  const previousSrcdocRef = useRef(srcdoc)
  useEffect(() => {
    if (previousSrcdocRef.current === srcdoc) {
      return
    }

    previousSrcdocRef.current = srcdoc

    if (!hasRenderableSourceRef.current) {
      setFrameSrcdoc('')
      return
    }

    if (frameElement && activeRef.current) {
      resetLoadingState()
    }
  }, [frameElement, resetLoadingState, srcdoc])

  const handleLoad = useCallback((): void => {
    if (!hasRenderableSourceRef.current || !activeRef.current || !frameSrcdocRef.current) {
      return
    }

    loadedRef.current = true
    setLoaded(true)
    setShowSkeleton(false)
  }, [])

  const handlePrint = useCallback((): void => {
    if (!loadedRef.current) {
      toast.error('The HTML preview is not ready to print')
      return
    }

    try {
      if (!frameRegistry.requestPrint(frameId)) {
        toast.error('The HTML preview is not ready to print')
      }
    } catch {
      toast.error('Unable to open the print dialog')
    }
  }, [frameId, frameRegistry])

  return {
    frameRef: setFrameElement,
    frameSrcdoc,
    frameHeight,
    reportedHeight,
    hasReportedHeight,
    loaded,
    showSkeleton,
    handleLoad,
    handlePrint,
  }
}

export default useHtmlBlockFrame
